import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {getEnumKeyByEnumValue, Language, LocaleLanguage} from './i18nTypes';
import {getCurrentLanguage} from './i18n-service';
import i18n from 'i18next';
import {LocalizationProvider as MuiLocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/en';

type NumberFormatOptions = {
    formatNumber: (value: any) => string;
    selectedLanguage: Language;
}

const NumberFormatContext = createContext<NumberFormatOptions>({
    formatNumber: (value: any) => String(value),
    selectedLanguage: Language.DEFAULT,
});

// Create a provider component
export const LocalizationProvider = ({children}) => {
    const [fractionDigits] = useState<{
        minimumFractionDigits: number,
        maximumFractionDigits: number
    }>({minimumFractionDigits: 0, maximumFractionDigits: 10});
    const selectedLanguage = getCurrentLanguage();
    const languageTrimmed = selectedLanguage.replace(/['"]+/g, '');
    const langEnum: Language = Language[getEnumKeyByEnumValue(Language, languageTrimmed)];
    const locale = useMemo(() => LocaleLanguage.getLocaleLanguage(selectedLanguage).locale, [selectedLanguage]);

    useEffect(() => {
        if (i18n.language !== langEnum) {
            i18n.changeLanguage(langEnum)
                .then(() => undefined)
                .catch((error) => {
                    console.error('failed to change language', error);
                });
        }
    }, [langEnum]);

    const formatNumber = useCallback((value) => {
        return new Intl.NumberFormat(locale, fractionDigits).format(value);
    }, [locale, fractionDigits]);

    return (
        <MuiLocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={toDayjsLocale(selectedLanguage)}>
            <NumberFormatContext.Provider value={{formatNumber, selectedLanguage}}>
                {children}
            </NumberFormatContext.Provider>
        </MuiLocalizationProvider>
    );
};

// Custom hook to use the number format context
export const useLocalizationUtil = () => {
    return useContext(NumberFormatContext);
};

function toDayjsLocale(selectedLanguage: string) {
    if (selectedLanguage.toLowerCase().startsWith('de')) return 'de';
    if (selectedLanguage.toLowerCase().startsWith('en')) return 'en';
    return 'de'; // fallback
}
